import type { UseFormRegister } from "react-hook-form";
import type { LucideIcon } from "lucide-react";
import type { CreateSharedLinkInput } from "@shared/validation/sharedLink.schema";
import {
  User2,
  Phone,
  HeartPulse,
  Pill,
  Sparkles,
  Clock,
  Utensils,
  Stethoscope,
  UserCheck,
} from "lucide-react";
import { IconChip } from "../ui/IconChip";
import { SECTION_ACCENT, type AccentName } from "../../lib/accents";
import { PICKUP_CONSENT_ENABLED } from "@shared/constants/features";

interface SectionOption {
  key: keyof CreateSharedLinkInput["visibleSections"];
  label: string;
  description: string;
  icon: LucideIcon;
  accent: AccentName;
}

const ALL_SECTIONS: SectionOption[] = [
  {
    key: "basic",
    label: "Basic Info",
    // The nickname is on every care link whatever is ticked (scopeStripper).
    description: "Age, grade, school, general notes. The nickname is always shown",
    icon: User2,
    accent: SECTION_ACCENT.basic,
  },
  {
    key: "emergencyContacts",
    label: "Emergency Contacts",
    description: "Primary and secondary contact phone + relationship",
    icon: Phone,
    accent: SECTION_ACCENT.emergency,
  },
  {
    key: "allergies",
    label: "Allergies",
    description: "Allergens, severity, emergency actions",
    icon: HeartPulse,
    accent: SECTION_ACCENT.allergies,
  },
  {
    key: "medications",
    label: "Medications",
    description: "Dosage, frequency, expiration",
    icon: Pill,
    accent: SECTION_ACCENT.medications,
  },
  {
    key: "carePreferences",
    label: "Care Preferences",
    description: "Behavioral notes, calming strategies, comfort items",
    icon: Sparkles,
    accent: SECTION_ACCENT.care,
  },
  {
    key: "routines",
    label: "Daily Routines",
    description: "Nap, meal, activity and bedtime schedule",
    icon: Clock,
    accent: SECTION_ACCENT.routines,
  },
  {
    key: "feeding",
    label: "Feeding & Diet",
    description: "Diet, likes and dislikes, portions, feeding schedule",
    icon: Utensils,
    accent: SECTION_ACCENT.feeding,
  },
  {
    key: "medical",
    label: "Medical & Immunizations",
    description: "Physician, conditions, blood type, vaccination records",
    icon: Stethoscope,
    accent: SECTION_ACCENT.medical,
  },
  {
    key: "pickup",
    label: "Pickup & Authorization",
    description: "Who may collect the child, consent to treat, insurance details",
    icon: UserCheck,
    accent: SECTION_ACCENT.pickup,
  },
];

// Pickup & Authorization is offered only while PICKUP_CONSENT_ENABLED is on;
// off, the form keeps its `pickup: false` default and the server stores false.
const SECTIONS = ALL_SECTIONS.filter(
  (section) => PICKUP_CONSENT_ENABLED || section.key !== "pickup",
);

interface VisibleSectionsPickerProps {
  register: UseFormRegister<CreateSharedLinkInput>;
}

export function VisibleSectionsPicker({ register }: VisibleSectionsPickerProps) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-neutral-800">
        Information Shared
      </legend>
      <p className="text-xs text-neutral-500">
        Choose exactly what information to share with each person. Medical info
        for nurses, comfort details for babysitters.
      </p>
      <div className="space-y-2">
        {SECTIONS.map(({ key, label, description, icon, accent }) => (
          <label
            key={key}
            className="flex cursor-pointer items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-3 transition hover:border-neutral-300 hover:bg-neutral-50"
          >
            <input
              type="checkbox"
              {...register(`visibleSections.${key}`)}
              className="h-4 w-4 rounded border-neutral-300 accent-sky-600"
            />
            <IconChip icon={icon} accent={accent} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-neutral-900">{label}</p>
              <p className="text-xs text-neutral-600">{description}</p>
            </div>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
